/**
 * TrackButton — drop-in "Quick Track" icon button. Renders a small
 * amber-tinted pill that opens the <QuickTrackDrawer /> for the given
 * candidate id. Keeps drawer state locally so each row's button is
 * self-contained — no context provider required.
 *
 * Usage:
 *   <TrackButton candidateId={c.id} testId={`track-${c.id}`} />
 *
 * Variants:
 *   - default: rectangular pill with "Track" label (list views).
 *   - variant="icon": 24x24 icon-only (dense kanban cards / feed rows).
 */
import { useState } from "react";
import QuickTrackDrawer from "@/components/QuickTrackDrawer";
import { Sparkles } from "lucide-react";

export default function TrackButton({ candidateId, testId, variant = "default", className = "" }) {
    const [open, setOpen] = useState(false);
    const isIcon = variant === "icon";
    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    // Trigger buttons often live inside clickable rows/cards —
                    // stop the event so opening the drawer doesn't also open
                    // the candidate profile / expand the kanban card.
                    e.stopPropagation();
                    // Phase 158 — snap any live hovercard shut so the Track
                    // drawer isn't layered under a lingering popover.
                    try { window.dispatchEvent(new Event("pw-hovercard-close")); } catch { /* ignore */ }
                    setOpen(true);
                }}
                className={`inline-flex items-center gap-1 border transition-colors ${
                    isIcon
                        ? "w-6 h-6 justify-center border-amber-300 hover:border-amber-500 hover:bg-amber-50 text-amber-700"
                        : "px-2 h-6 text-[10px] font-mono uppercase tracking-widest border-amber-400 bg-white hover:bg-amber-50 text-amber-800"
                } ${className}`}
                data-testid={testId || `track-btn-${candidateId}`}
                aria-label="Open quick-track panel"
                title="Quick track (salary, follow-ups, interviews, scores)"
            >
                <Sparkles className={isIcon ? "w-3.5 h-3.5" : "w-3 h-3"} />
                {!isIcon && "Track"}
            </button>
            <QuickTrackDrawer
                candidateId={candidateId}
                open={open}
                onClose={() => setOpen(false)}
            />
        </>
    );
}
